import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useAiSettings, useUpdateAiSettings } from '@/features/ai-settings/api';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';

export const Route = createFileRoute('/_authed/ai/settings/')({
  component: AiSettingsPage,
});

// Keep in sync with the pricing table in apps/api/src/lib/llm-pricing.ts —
// a slug used in production without a pricing entry logs cost as 0.
const MODEL_OPTIONS = [
  { value: 'anthropic/claude-sonnet-5', label: 'Claude Sonnet 5 (Anthropic)' },
  { value: 'anthropic/claude-haiku-4-5', label: 'Claude Haiku 4.5 (Anthropic)' },
  { value: 'openai/gpt-5.6-terra', label: 'OpenAI GPT-5.6 Terra' },
  { value: 'openai/gpt-5.6-luna', label: 'OpenAI GPT-5.6 Luna' },
  { value: 'google/gemini-3.5-flash', label: 'Google Gemini 3.5 Flash' },
  { value: 'google/gemini-3.1-flash-lite', label: 'Google Gemini 3.1 Flash Lite' },
  { value: 'minimax/m3', label: 'MiniMax M3' },
  { value: 'minimax/m2.7', label: 'MiniMax M2.7' },
  { value: 'openai/gpt-image-2', label: 'OpenAI gpt-image-2 (image gen)' },
];

// Draft fallback rows hold raw string field values so partially-typed numbers
// don't fight the inputs; they're validated + coerced on save.
type TextFallbackDraft = { model: string; temperature: string; maxTokens: string };
type VisionFallbackDraft = { model: string; maxTokens: string };
type ImageFallbackDraft = { model: string; aspectRatio: string; size: string; quality: string };

function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/** Shared chrome for one fallback row: order label + up/down/remove controls. */
function FallbackRow(props: {
  testId: string;
  index: number;
  count: number;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  children: React.ReactNode;
}) {
  const { testId, index, count, onMove, onRemove, children } = props;
  return (
    <div
      data-testid={testId}
      className="rounded-lg border border-black/10 p-3 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs text-ink-soft font-mono">Fallback {index + 1}</span>
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid={`${testId}-move-up`}
            disabled={index === 0}
            onClick={() => onMove(-1)}
            aria-label="Move up"
          >
            ↑
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid={`${testId}-move-down`}
            disabled={index === count - 1}
            onClick={() => onMove(1)}
            aria-label="Move down"
          >
            ↓
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid={`${testId}-remove`}
            onClick={onRemove}
            aria-label="Remove fallback"
          >
            Remove
          </Button>
        </div>
      </div>
      {children}
    </div>
  );
}

function FallbackListHeader(props: { testId: string; onAdd: () => void }) {
  return (
    <div className="flex items-center justify-between mt-1">
      <div>
        <h3 className="text-sm font-medium">Fallback models</h3>
        <p className="text-ink-soft text-xs mt-0.5">
          Tried in order when the primary model fails (the primary is retried once first).
        </p>
      </div>
      <Button type="button" variant="ghost" size="sm" data-testid={props.testId} onClick={props.onAdd}>
        + Add fallback
      </Button>
    </div>
  );
}

function AiSettingsPage() {
  const { data: settings, isLoading } = useAiSettings();
  const update = useUpdateAiSettings();

  const [model, setModel] = useState('');
  const [visionModel, setVisionModel] = useState('');
  const [visionMaxTokens, setVisionMaxTokens] = useState('');
  const [imageModel, setImageModel] = useState('');
  const [imageAspectRatio, setImageAspectRatio] = useState('');
  const [imageSize, setImageSize] = useState('');
  const [imageQuality, setImageQuality] = useState('');
  const [temperature, setTemperature] = useState('');
  const [maxTokens, setMaxTokens] = useState('');
  const [editModel, setEditModel] = useState('');
  const [editTemperature, setEditTemperature] = useState('');
  const [editMaxTokens, setEditMaxTokens] = useState('');
  const [fallbacks, setFallbacks] = useState<TextFallbackDraft[]>([]);
  const [editFallbacks, setEditFallbacks] = useState<TextFallbackDraft[]>([]);
  const [visionFallbacks, setVisionFallbacks] = useState<VisionFallbackDraft[]>([]);
  const [imageFallbacks, setImageFallbacks] = useState<ImageFallbackDraft[]>([]);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setModel(settings.model);
      setVisionModel(settings.visionModel);
      setVisionMaxTokens(String(settings.visionMaxTokens));
      setImageModel(settings.imageModel);
      setImageAspectRatio(settings.imageAspectRatio);
      setImageSize(settings.imageSize);
      setImageQuality(settings.imageQuality);
      setTemperature(String(settings.temperature));
      setMaxTokens(String(settings.maxTokens));
      setEditModel(settings.editModel ?? '');
      setEditTemperature(settings.editTemperature > 0 ? String(settings.editTemperature) : '');
      setEditMaxTokens(settings.editMaxTokens > 0 ? String(settings.editMaxTokens) : '');
      setFallbacks((settings.fallbackModels ?? []).map((f) => ({
        model: f.model,
        temperature: String(f.temperature),
        maxTokens: String(f.maxTokens),
      })));
      setEditFallbacks((settings.editFallbackModels ?? []).map((f) => ({
        model: f.model,
        temperature: String(f.temperature),
        maxTokens: String(f.maxTokens),
      })));
      setVisionFallbacks((settings.visionFallbackModels ?? []).map((f) => ({
        model: f.model,
        maxTokens: String(f.maxTokens),
      })));
      setImageFallbacks((settings.imageFallbackModels ?? []).map((f) => ({
        model: f.model,
        aspectRatio: f.aspectRatio,
        size: f.size,
        quality: f.quality,
      })));
    }
  }, [settings]);

  const validateTextFallbacks = (
    drafts: TextFallbackDraft[],
    label: string,
  ): { model: string; temperature: number; maxTokens: number }[] | null => {
    const out: { model: string; temperature: number; maxTokens: number }[] = [];
    for (const [i, d] of drafts.entries()) {
      const temp = Number(d.temperature);
      const mtok = Number(d.maxTokens);
      if (!d.model.trim()) {
        setError(`${label} fallback ${i + 1}: model is required`);
        return null;
      }
      if (d.temperature.trim() === '' || Number.isNaN(temp) || temp < 0 || temp > 2) {
        setError(`${label} fallback ${i + 1}: temperature must be between 0 and 2`);
        return null;
      }
      if (!Number.isInteger(mtok) || mtok < 1) {
        setError(`${label} fallback ${i + 1}: max tokens must be a positive integer`);
        return null;
      }
      out.push({ model: d.model.trim(), temperature: temp, maxTokens: mtok });
    }
    return out;
  };

  const handleSave = async () => {
    setError(null);
    setSaveMsg(null);
    const temp = Number(temperature);
    const mtok = Number(maxTokens);
    if (Number.isNaN(temp) || temp < 0 || temp > 2) {
      setError('Temperature must be between 0 and 2');
      return;
    }
    if (!Number.isInteger(mtok) || mtok < 1) {
      setError('Max tokens must be a positive integer');
      return;
    }
    const visMtok = Number(visionMaxTokens);
    if (!Number.isInteger(visMtok) || visMtok < 1) {
      setError('Vision max tokens must be a positive integer');
      return;
    }
    const editTemp = editTemperature.trim() === '' ? 0 : Number(editTemperature);
    const editMtok = editMaxTokens.trim() === '' ? 0 : Number(editMaxTokens);
    if (editTemperature.trim() !== '' && (Number.isNaN(editTemp) || editTemp < 0 || editTemp > 2)) {
      setError('Edit temperature must be between 0 and 2 (or blank to inherit)');
      return;
    }
    if (editMaxTokens.trim() !== '' && (!Number.isInteger(editMtok) || editMtok < 1)) {
      setError('Edit max tokens must be a positive integer (or blank to inherit)');
      return;
    }

    const fallbackModels = validateTextFallbacks(fallbacks, 'Generation');
    if (!fallbackModels) return;
    const editFallbackModels = validateTextFallbacks(editFallbacks, 'Edit');
    if (!editFallbackModels) return;

    const visionFallbackModels: { model: string; maxTokens: number }[] = [];
    for (const [i, d] of visionFallbacks.entries()) {
      const vMtok = Number(d.maxTokens);
      if (!d.model.trim()) {
        setError(`Vision fallback ${i + 1}: model is required`);
        return;
      }
      if (!Number.isInteger(vMtok) || vMtok < 1) {
        setError(`Vision fallback ${i + 1}: max tokens must be a positive integer`);
        return;
      }
      visionFallbackModels.push({ model: d.model.trim(), maxTokens: vMtok });
    }

    const imageFallbackModels: { model: string; aspectRatio: string; size: string; quality: string }[] = [];
    for (const [i, d] of imageFallbacks.entries()) {
      if (!d.model.trim()) {
        setError(`Image fallback ${i + 1}: model is required`);
        return;
      }
      imageFallbackModels.push({
        model: d.model.trim(),
        aspectRatio: d.aspectRatio.trim(),
        size: d.size.trim(),
        quality: d.quality.trim(),
      });
    }

    try {
      await update.mutateAsync({
        model,
        visionModel,
        visionMaxTokens: visMtok,
        imageModel,
        imageAspectRatio,
        imageSize,
        imageQuality,
        temperature: temp,
        maxTokens: mtok,
        editModel,
        editTemperature: editTemp,
        editMaxTokens: editMtok,
        fallbackModels,
        editFallbackModels,
        visionFallbackModels,
        imageFallbackModels,
      });
      setSaveMsg('Saved');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  if (isLoading) return <div className="p-4 sm:p-6 lg:p-8 text-ink-soft">Loading…</div>;

  const renderTextFallbacks = (
    drafts: TextFallbackDraft[],
    setDrafts: React.Dispatch<React.SetStateAction<TextFallbackDraft[]>>,
    idPrefix: string,
  ) => (
    <>
      <FallbackListHeader
        testId={`${idPrefix}-add`}
        onAdd={() => setDrafts((d) => [...d, { model: '', temperature: '0.4', maxTokens: '4000' }])}
      />
      {drafts.map((d, i) => (
        <FallbackRow
          key={i}
          testId={`${idPrefix}-${i}`}
          index={i}
          count={drafts.length}
          onMove={(dir) => setDrafts((list) => moveItem(list, i, i + dir))}
          onRemove={() => setDrafts((list) => list.filter((_, j) => j !== i))}
        >
          <Input
            label="Model"
            list="ai-model-suggestions"
            value={d.model}
            onChange={(e) => setDrafts((list) => list.map((x, j) => (j === i ? { ...x, model: e.target.value } : x)))}
            placeholder="provider/model-slug"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Temperature"
              type="number"
              step="0.05"
              min="0"
              max="2"
              value={d.temperature}
              onChange={(e) => setDrafts((list) => list.map((x, j) => (j === i ? { ...x, temperature: e.target.value } : x)))}
            />
            <Input
              label="Max tokens"
              type="number"
              step="100"
              min="1"
              value={d.maxTokens}
              onChange={(e) => setDrafts((list) => list.map((x, j) => (j === i ? { ...x, maxTokens: e.target.value } : x)))}
            />
          </div>
        </FallbackRow>
      ))}
    </>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <header className="mb-6">
        <h1 className="font-serif text-3xl leading-tight">AI settings</h1>
        <p className="text-ink-soft text-sm mt-1">Model and sampling parameters.</p>
      </header>

      <datalist id="ai-model-suggestions">
        {MODEL_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </datalist>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left column: Recipe generation + Recipe editing */}
        <div className="flex flex-col gap-6">
          {/* Recipe generation card */}
          <Card
            shadow="sm"
            className="p-4 sm:p-6 flex flex-col gap-5"
            data-testid="ai-settings-card-recipe-generation"
            data-column="left"
          >
            <header>
              <h2 className="font-serif text-xl">Recipe generation</h2>
              <p className="text-ink-soft text-sm mt-0.5">
                Primary model and sampling settings used to synthesise recipe JSON.
              </p>
            </header>

            <Input
              label="Model"
              list="ai-model-suggestions"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="provider/model-slug (e.g. aws-bedrock/claude-sonnet-4-6)"
              hint="Any LLM Gateway model slug. Suggestions autocomplete from the list — type freely for anything else."
            />

            <Input
              label="Temperature"
              type="number"
              step="0.05"
              min="0"
              max="2"
              value={temperature}
              onChange={(e) => setTemperature(e.target.value)}
              hint="0 = deterministic, 1 = normal, 2 = wild. Recipes work best around 0.3-0.6."
            />

            <Input
              label="Max tokens"
              type="number"
              step="100"
              min="1"
              value={maxTokens}
              onChange={(e) => setMaxTokens(e.target.value)}
              hint="Model output token budget. 4000 comfortably fits a full recipe; set as high as the model allows."
            />

            {renderTextFallbacks(fallbacks, setFallbacks, 'ai-settings-gen-fallback')}
          </Card>

          {/* Recipe editing card */}
          <Card
            shadow="sm"
            className="p-4 sm:p-6 flex flex-col gap-5"
            data-testid="ai-settings-card-recipe-editing"
            data-column="left"
          >
            <header>
              <h2 className="font-serif text-xl">Recipe editing</h2>
              <p className="text-ink-soft text-sm mt-0.5">
                Override the model used for recipe edits (<code className="font-mono text-xs">POST /api/recipes/:id/edit</code>).
                Leave blank to reuse the generation settings.
              </p>
            </header>

            <Input
              label="Edit model"
              list="ai-model-suggestions"
              value={editModel}
              onChange={(e) => setEditModel(e.target.value)}
              placeholder="Inherit generation model (leave blank)"
              hint="When blank, the generation model is used. Set to any LLM Gateway model slug to override for edit calls only."
            />

            <Input
              label="Edit temperature"
              type="number"
              step="0.05"
              min="0"
              max="2"
              value={editTemperature}
              onChange={(e) => setEditTemperature(e.target.value)}
              placeholder="Inherit generation temperature"
              hint="When blank, the generation temperature is used. 0–2; lower values produce more conservative edits."
            />

            <Input
              label="Edit max tokens"
              type="number"
              step="100"
              min="1"
              value={editMaxTokens}
              onChange={(e) => setEditMaxTokens(e.target.value)}
              placeholder="Inherit generation max tokens"
              hint="When blank, the generation max-tokens value is used."
            />

            {renderTextFallbacks(editFallbacks, setEditFallbacks, 'ai-settings-edit-fallback')}
          </Card>
        </div>

        {/* Right column: Image generation + Vision extraction */}
        <div className="flex flex-col gap-6">
          {/* Image generation card */}
          <Card
            shadow="sm"
            className="p-4 sm:p-6 flex flex-col gap-5"
            data-testid="ai-settings-card-image-gen"
            data-column="right"
          >
            <header>
              <h2 className="font-serif text-xl">Image generation</h2>
              <p className="text-ink-soft text-sm mt-0.5">Thumbnail rendering for AI-generated recipes.</p>
            </header>

            <Input
              list="ai-model-suggestions"
              label="Image generation model"
              placeholder="(empty — image generation disabled)"
              hint="LLM Gateway slug for the image-generation stage. When set, AI-generated recipes get a thumbnail rendered after synthesis. Empty disables it."
              value={imageModel}
              onChange={(e) => setImageModel(e.target.value)}
            />

            <Input
              label="Aspect ratio"
              placeholder="4:3"
              hint="Forwarded as image_config.aspect_ratio to LLM Gateway when non-empty."
              value={imageAspectRatio}
              onChange={(e) => setImageAspectRatio(e.target.value)}
            />

            <Input
              label="Image size"
              placeholder="1024x768"
              hint="Forwarded as image_config.size to LLM Gateway when non-empty."
              value={imageSize}
              onChange={(e) => setImageSize(e.target.value)}
            />

            <Input
              label="Image quality"
              placeholder="(leave empty for default)"
              hint="Forwarded as image_config.quality to LLM Gateway when non-empty."
              value={imageQuality}
              onChange={(e) => setImageQuality(e.target.value)}
            />

            <FallbackListHeader
              testId="ai-settings-image-fallback-add"
              onAdd={() => setImageFallbacks((d) => [...d, { model: '', aspectRatio: '', size: '', quality: '' }])}
            />
            {imageFallbacks.map((d, i) => (
              <FallbackRow
                key={i}
                testId={`ai-settings-image-fallback-${i}`}
                index={i}
                count={imageFallbacks.length}
                onMove={(dir) => setImageFallbacks((list) => moveItem(list, i, i + dir))}
                onRemove={() => setImageFallbacks((list) => list.filter((_, j) => j !== i))}
              >
                <Input
                  label="Model"
                  list="ai-model-suggestions"
                  value={d.model}
                  onChange={(e) => setImageFallbacks((list) => list.map((x, j) => (j === i ? { ...x, model: e.target.value } : x)))}
                  placeholder="provider/model-slug"
                />
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    label="Aspect ratio"
                    placeholder="4:3"
                    value={d.aspectRatio}
                    onChange={(e) => setImageFallbacks((list) => list.map((x, j) => (j === i ? { ...x, aspectRatio: e.target.value } : x)))}
                  />
                  <Input
                    label="Size"
                    placeholder="1024x768"
                    value={d.size}
                    onChange={(e) => setImageFallbacks((list) => list.map((x, j) => (j === i ? { ...x, size: e.target.value } : x)))}
                  />
                  <Input
                    label="Quality"
                    placeholder="(default)"
                    value={d.quality}
                    onChange={(e) => setImageFallbacks((list) => list.map((x, j) => (j === i ? { ...x, quality: e.target.value } : x)))}
                  />
                </div>
              </FallbackRow>
            ))}
          </Card>

          {/* Vision extraction card */}
          <Card
            shadow="sm"
            className="p-4 sm:p-6 flex flex-col gap-5"
            data-testid="ai-settings-card-vision"
            data-column="right"
          >
            <header>
              <h2 className="font-serif text-xl">Vision extraction</h2>
              <p className="text-ink-soft text-sm mt-0.5">
                Optional two-stage pipeline: a vision model extracts a structured image
                description before synthesis. The vision prompt is bundled in code (see AI
                Prompts page).
              </p>
            </header>

            <Input
              label="Vision extraction model"
              list="ai-model-suggestions"
              value={visionModel}
              onChange={(e) => setVisionModel(e.target.value)}
              placeholder="(empty — two-stage disabled)"
              hint="When set, image-bearing requests run two-stage: this model extracts a structured image description, then the primary model synthesises the recipe from text only."
            />

            <Input
              label="Vision max tokens"
              type="number"
              step="100"
              min="1"
              value={visionMaxTokens}
              onChange={(e) => setVisionMaxTokens(e.target.value)}
              hint="Output-token budget for the extraction call. Keep this generous — multi-page transcriptions plus a thinking model's hidden reasoning need headroom, or the Gateway rejects the truncated response ('Failed to parse model output'). 4000 is a safe floor."
            />

            <FallbackListHeader
              testId="ai-settings-vision-fallback-add"
              onAdd={() => setVisionFallbacks((d) => [...d, { model: '', maxTokens: '4000' }])}
            />
            {visionFallbacks.map((d, i) => (
              <FallbackRow
                key={i}
                testId={`ai-settings-vision-fallback-${i}`}
                index={i}
                count={visionFallbacks.length}
                onMove={(dir) => setVisionFallbacks((list) => moveItem(list, i, i + dir))}
                onRemove={() => setVisionFallbacks((list) => list.filter((_, j) => j !== i))}
              >
                <Input
                  label="Model"
                  list="ai-model-suggestions"
                  value={d.model}
                  onChange={(e) => setVisionFallbacks((list) => list.map((x, j) => (j === i ? { ...x, model: e.target.value } : x)))}
                  placeholder="provider/model-slug"
                />
                <Input
                  label="Max tokens"
                  type="number"
                  step="100"
                  min="1"
                  value={d.maxTokens}
                  onChange={(e) => setVisionFallbacks((list) => list.map((x, j) => (j === i ? { ...x, maxTokens: e.target.value } : x)))}
                />
              </FallbackRow>
            ))}
          </Card>
        </div>
      </div>

      {error && (
        <div
          className="mt-6 text-xs rounded-lg p-3"
          style={{
            background: 'var(--color-error-bg)',
            border: '1px solid var(--color-error-border)',
            color: 'var(--color-error-text)',
          }}
        >
          {error}
        </div>
      )}
      {saveMsg && <div className="mt-4 text-xs text-ink-soft">{saveMsg}</div>}

      <div className="mt-6 flex justify-end gap-2">
        <Button onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
